import { CriteriaRentType, Money } from "shared/types";
import { PreviewApplicationRentByUnit } from "types/application";

export type RentOption = "use_current" | "use_listed" | "manual";

export type PreviewStatus = "pending" | "error" | "success";

interface ManualRentsInput {
  rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
}

export interface RentData {
  change_type: RentOption;
  rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
}

export const getPreviewStatus = ({
  isFetching,
  isError,
  isSuccess
}: {
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
}): PreviewStatus => {
  if (isFetching && !isSuccess) return "pending";
  if (isError) return "error";
  if (isSuccess) return "success";
  return "pending";
};

export const formatListedRentDescription = (unitName: string) =>
  /^unit\b/i.test(unitName.trim()) ? `For ${unitName}` : `For Unit ${unitName}`;

const isPositive = (amount?: Money | null) => (amount?.cents ?? 0) > 0;

/**
 * Whether the destination unit has a listed rent worth defaulting to.
 *
 * Presence is not enough: a unit can come back with a zero component, and
 * `UpdateUnit` would then write that zero onto the application. Which
 * component has to be positive depends on `criteria_rent_type`, because that
 * is what the application rent is drawn from.
 */
export const hasListedRent = (
  preview: PreviewApplicationRentByUnit | undefined,
  homeSiteRentEnabled: boolean
) => {
  if (!preview) return false;
  if (!homeSiteRentEnabled) return isPositive(preview.rent_amount);

  // Mirrors the use_listed guard in UpdateUnit: it only writes when the unit
  // carries both components.
  if (preview.home_rent_amount == null || preview.site_rent_amount == null)
    return false;

  const criteria = preview.criteria_rent_type || CriteriaRentType.Both;
  if (criteria === CriteriaRentType.Home)
    return isPositive(preview.home_rent_amount);
  if (criteria === CriteriaRentType.Site)
    return isPositive(preview.site_rent_amount);
  return (
    isPositive(preview.home_rent_amount) || isPositive(preview.site_rent_amount)
  );
};

/**
 * Without a preview the destination's criteria rent type is unknown, so there
 * is nothing to compare against. Assuming `Both` disabled "Use current rent"
 * on every application whose own criteria differed, which locked those users
 * out of the move whenever the preview failed. Treat unknown as allowed and
 * let the server reject a genuine mismatch.
 */
export const isUseCurrentAllowed = (
  currentCriteriaRentType: CriteriaRentType,
  preview?: PreviewApplicationRentByUnit
) => {
  if (!preview) return true;
  return (
    currentCriteriaRentType ===
    (preview.criteria_rent_type || CriteriaRentType.Both)
  );
};

export const resolveDefaultRentChangeType = ({
  canEditApplicationRent,
  previewStatus,
  hasListedRent: listedRentAvailable,
  useCurrentAllowed
}: {
  canEditApplicationRent: boolean;
  previewStatus: PreviewStatus;
  hasListedRent: boolean;
  useCurrentAllowed: boolean;
}): RentOption => {
  if (!canEditApplicationRent) return "use_listed";
  if (previewStatus === "pending") return "use_listed";
  if (previewStatus === "success" && listedRentAvailable) return "use_listed";
  if (useCurrentAllowed) return "use_current";
  return "manual";
};

export const buildRentData = ({
  rentChangeType,
  manualRentsInput,
  homeSiteRentEnabled,
  preview
}: {
  rentChangeType: RentOption;
  manualRentsInput?: ManualRentsInput;
  homeSiteRentEnabled: boolean;
  preview?: PreviewApplicationRentByUnit;
}): RentData => {
  if (rentChangeType !== "manual") return { change_type: rentChangeType };

  if (!homeSiteRentEnabled)
    return {
      change_type: rentChangeType,
      rent_amount: manualRentsInput?.rent_amount
    };

  return {
    change_type: rentChangeType,
    home_rent_amount:
      preview?.criteria_rent_type !== CriteriaRentType.Site
        ? manualRentsInput?.home_rent_amount
        : preview.home_rent_amount,
    site_rent_amount:
      preview?.criteria_rent_type !== CriteriaRentType.Home
        ? manualRentsInput?.site_rent_amount
        : preview.site_rent_amount
  };
};
